import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { API_BASE_URL, getAuthHeaders } from "../../utils/api";
import TeacherAcademicHeader from "./TeacherAcademicHeader";

function TeacherHomeworkDetail({ courses = [], loading: parentLoading }) {
  const navigate = useNavigate();
  const { assignmentId, homeworkId } = useParams();
  const location = useLocation();

  const lockedCourse =
    location.state?.course ||
    courses.find((c) => String(c.id) === String(assignmentId)) ||
    null;

  const [hw, setHw] = useState(location.state?.homework || null);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(!location.state?.homework);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [targetMode, setTargetMode] = useState("class");
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [file, setFile] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (location.state?.homework) {
        const h = location.state.homework;
        setTitle(h.title || "");
        setDescription(h.description || "");
        setDueDate(h.due_date || "");
        setTargetMode(h.target_mode || "class");
        setSelectedStudents(h.student_ids || []);
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const res = await fetch(`${API_BASE_URL}/api/teachers/me/homework/${homeworkId}`, {
          headers: getAuthHeaders(),
        });
        if (!res.ok) throw new Error("Homework not found");
        const data = await res.json();
        if (!alive) return;
        setHw(data);
        setTitle(data.title || "");
        setDescription(data.description || "");
        setDueDate(data.due_date || "");
        setTargetMode(data.target_mode || "class");
        setSelectedStudents(data.student_ids || []);
      } catch (err) {
        if (alive) alert(err.message || "Failed to load homework");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [homeworkId, location.state?.homework]);

  useEffect(() => {
    const cls = hw?.class_id || lockedCourse?.class_id;
    const crs = hw?.course_id || lockedCourse?.course_id;
    if (!cls) return;
    (async () => {
      const params = new URLSearchParams({ class_id: cls });
      if (crs) params.set("course_id", crs);
      const res = await fetch(`${API_BASE_URL}/api/teachers/me/students?${params}`, {
        headers: getAuthHeaders(),
      });
      const data = res.ok ? await res.json() : [];
      setStudents(Array.isArray(data) ? data : []);
    })();
  }, [hw?.class_id, hw?.course_id, lockedCourse?.class_id, lockedCourse?.course_id]);

  const toggleStudent = (id) => {
    setSelectedStudents((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const save = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("title", title.trim());
      if (description.trim()) fd.append("description", description.trim());
      if (dueDate) fd.append("due_date", dueDate);
      fd.append("target_mode", targetMode);
      if (targetMode === "selected") {
        fd.append("student_ids", selectedStudents.join(","));
      }
      if (file) fd.append("file", file);

      const res = await fetch(`${API_BASE_URL}/api/teachers/me/homework/${homeworkId}`, {
        method: "PUT",
        headers: getAuthHeaders(),
        body: fd,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Failed to update homework");
      }
      const data = await res.json();
      setHw(data);
      setFile(null);
      alert("Homework updated.");
    } catch (err) {
      alert(err.message || "Failed to update homework");
    } finally {
      setSaving(false);
    }
  };

  const back = () => {
    if (assignmentId && assignmentId !== "0") {
      navigate(`/teacher/courses/${assignmentId}/homework`, { state: { course: lockedCourse } });
    } else {
      navigate("/teacher/courses");
    }
  };

  if (parentLoading || loading) {
    return (
      <div className="tp-loading">
        <div className="tp-spinner" />
        <p>Loading homework…</p>
      </div>
    );
  }

  if (!hw) {
    return (
      <div className="tp-empty">
        <h3>Homework not found</h3>
        <button type="button" className="tp-btn tp-btn--ghost" onClick={back}>← Back</button>
      </div>
    );
  }

  const assignedNames =
    hw.student_names?.length
      ? hw.student_names
      : (hw.student_ids || [])
          .map((id) => students.find((s) => s.id === id)?.name)
          .filter(Boolean);

  return (
    <div className="tp-academic-shell">
      <TeacherAcademicHeader
        title={hw.title}
        subtitle={`${hw.course_name} · ${hw.class_name}${hw.session_name ? ` · Session ${hw.session_name}` : ""}`}
        backLabel="Back to homework"
        onBack={back}
      />

      <div className="tp-academic-panel">
        <div className="tp-academic-panel__head">
          <h2>Assignment details</h2>
        </div>
        <div className="tp-detail-inline">
          <p><strong>Target:</strong> {hw.target_mode === "selected" ? "Selected students" : "Whole class"}</p>
          {assignedNames?.length ? <p><strong>Students:</strong> {assignedNames.join(", ")}</p> : null}
          <p><strong>Due:</strong> {hw.due_date || "—"}</p>
          {hw.description ? <p className="tp-remark-card__body">{hw.description}</p> : null}
          {hw.attachment_path ? (
            <p>
              <a href={`${API_BASE_URL}${hw.attachment_path}`} target="_blank" rel="noreferrer">
                {hw.attachment_name || "Download attachment"}
              </a>
            </p>
          ) : null}
        </div>
      </div>

      <div className="tp-academic-panel" style={{ marginTop: "1rem" }}>
        <div className="tp-academic-panel__head">
          <h2>Edit homework</h2>
        </div>
        <form onSubmit={save} className="tp-form-grid tp-form-grid--academic">
          <label>
            Title
            <input required value={title} onChange={(e) => setTitle(e.target.value)} />
          </label>
          <label>
            Due date
            <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
          </label>
          <label className="tp-form-full">
            Description
            <textarea
              className="tp-textarea tp-textarea--lg"
              rows={5}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Instructions and submission details…"
            />
          </label>
          <label>
            Assign to
            <select value={targetMode} onChange={(e) => setTargetMode(e.target.value)}>
              <option value="class">Entire class</option>
              <option value="selected">Selected students</option>
            </select>
          </label>
          <label>
            New attachment
            <input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </label>
          {targetMode === "selected" && (
            <div className="tp-form-full">
              <span className="tp-field-label">Students</span>
              <div className="tp-student-checks tp-student-checks--boxed">
                {students.map((s) => (
                  <label key={s.id}>
                    <input
                      type="checkbox"
                      checked={selectedStudents.includes(s.id)}
                      onChange={() => toggleStudent(s.id)}
                    />
                    {s.name}
                  </label>
                ))}
              </div>
            </div>
          )}
          <div className="tp-form-full tp-form-actions">
            <button type="submit" className="tp-btn tp-btn-primary" disabled={saving}>
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default TeacherHomeworkDetail;
